import useParityConfirm from '../common/useParityConfirm';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { YearContext } from "../../contexts/yearContext";
import MainTitle from "../common/MainTitle";
import Table from "../common/Table";
import AccessDeniedResponse from "../common/AccessDeniedResponse";
import {
  ActionGroup,
  EmptyState,
  FilterBar,
  LoadingState,
  SectionCard,
  StatusBadge,
} from "../common/SurfaceUI";
import db from "../../services/query";
import { errorMessage } from "./AttendanceAdmin";
import "./attendanceAdmin.css";
import ParityReport from '../common/ParityReport';
import { flashPrintBlocked, openPrintRoute } from '../prints/parity/AriaReport';

const warningLevel = (row, threshold) => {
  if (Number(row.unqualified)) return { label: "محروم", tone: "danger" };
  if (Number(row.absent_percentage) >= Number(threshold)) {
    return { label: "هشدار", tone: "warning" };
  }
  return { label: "عادی", tone: "success" };
};

const formatSemesterLabel = (item) => {
  if (!item) return "";
  return item.name || item.label || String(item.entry_year_id || "");
};

const formatSemesterDates = (item) => {
  if (!item?.start_date || !item?.end_date) return "";
  return `${item.start_date} تا ${item.end_date}`;
};

const Percentage = ({ row, threshold }) => {
  const percentage = Math.min(100, Math.max(0, Number(row.absent_percentage) || 0));
  const level = warningLevel(row, threshold);
  return (
    <div className={`warning-percentage is-${level.tone}`}>
      <div>
        <strong>{percentage}%</strong>
        <span>{row.absent_count}/{row.total_count} جلسه</span>
      </div>
      <span className="warning-percentage-track" aria-hidden="true">
        <i style={{ width: `${percentage}%` }} />
      </span>
    </div>
  );
};

export default function AbsenceWarnings() {
  const [confirm, confirmationDialog] = useParityConfirm();
  const year = useContext(YearContext);
  const [entryYearId, setEntryYearId] = useState(year?.entryYear || "");
  const [filters, setFilters] = useState({ warnPct: 20, status: "all", worstOnly: "1", search: "", dateFrom: "", dateTo: "", facultyId: "", classId: "" });
  const [options, setOptions] = useState({ semesters: [], faculties: [], classes: [], semester: null });
  const [page, setPage] = useState(1);
  const [body, setBody] = useState({ data: [], summary: {} });
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const [message, setMessage] = useState("");
  const [printError, setPrintError] = useState("");
  const busyRef = useRef(false);
  const generation = useRef(0);
  useEffect(() => {
    if (year?.entryYear != null && String(year.entryYear) !== String(entryYearId)) {
      setEntryYearId(String(year.entryYear));
      setFilters(previous => ({ ...previous, classId: "" }));
      setPage(1);
    }
  }, [year?.entryYear]);
  useEffect(() => {
    let cancelled = false;
    db.getAll(`attendance/admin/warnings/options${entryYearId ? `?entryYearId=${entryYearId}` : ""}`)
      .then(({ data }) => { if (!cancelled && data?.summary) setOptions(data.summary); })
      .catch(err => { if (!cancelled) setMessage(errorMessage(err)); });
    return () => { cancelled = true; };
  }, [entryYearId]);
  const query = useCallback(() => {
    const params = new URLSearchParams({ entryYearId: String(entryYearId), page: String(page) });
    Object.entries(filters).forEach(([k, v]) => { if (v !== "") params.set(k, String(v)); });
    return params.toString();
  }, [entryYearId, filters, page]);
  const refresh = useCallback(async () => {
    if (!entryYearId) { generation.current += 1; setBody({ data: [], summary: {} }); setLoading(false); return false; }
    const current = ++generation.current; setLoading(true); setDenied(false);
    try {
      const { data } = await db.getAll(`attendance/admin/warnings?${query()}`);
      if (!Array.isArray(data?.data)) throw new Error("پاسخ سرور معتبر نیست.");
      if (current === generation.current) setBody(data);
      return true;
    } catch (err) {
      if (current === generation.current) {
        setDenied((err?.response?.status || err?.response?.data?.error?.statusCode) === 403);
        setMessage(errorMessage(err)); setBody({ data: [], summary: {} });
      }
      return false;
    } finally { if (current === generation.current) setLoading(false); }
  }, [entryYearId, query]);
  useEffect(() => { setDetail(null); setMessage(""); refresh(); return () => { generation.current += 1; }; }, [refresh]);
  const openDetail = async row => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setMessage("");
    try {
      const { data } = await db.getAll(`attendance/admin/warnings/students/${row.student_id}?${query()}`);
      setDetail({ ...data, studentId: row.student_id, name: row.student_name }); setNotes({});
    } catch (err) { setMessage(errorMessage(err)); }
    finally { busyRef.current = false; setBusy(false); }
  };
  const save = async row => {
    if (busyRef.current) return;
    const note = notes[row.subject_id] ?? row.note ?? "";
    if (!await confirm(`ثبت هشدار\n${detail.name}\n${row.subject_name}\n${note}`)) return;
    busyRef.current = true; setBusy(true); setMessage("");
    try {
      const { data } = await db.put("attendance/admin/warnings", { studentId: row.student_id, subjectId: row.subject_id, entryYearId: Number(entryYearId), note });
      const saved = data?.data?.[0];
      if (!data?.success || Number(saved?.student_id) !== Number(row.student_id) || Number(saved?.subject_id) !== Number(row.subject_id) || !Array.isArray(data.summary?.history)) throw new Error("پاسخ سرور معتبر نیست. دوباره بارگیری کنید.");
      setDetail(previous => ({ ...previous, data: previous.data.map(r => r.subject_id === row.subject_id ? { ...r, ...saved } : r), summary: { history: data.summary.history } }));
      setNotes(previous => ({ ...previous, [row.subject_id]: saved.note || "" }));
      const reloaded = await refresh();
      setMessage(reloaded ? "هشدار ثبت شد." : "ثبت شد؛ بارگیری مجدد ناموفق بود.");
    } catch (err) { setMessage(errorMessage(err)); }
    finally { busyRef.current = false; setBusy(false); }
  };
  const change = (key, value) => { setFilters(previous => ({ ...previous, [key]: value })); setPage(1); };
  const columns = [
    { key: "student", label: "محصل", content: row => <button className="warning-student-link" disabled={busy || loading} data-testid={`warning-detail-${row.student_id}`} onClick={() => openDetail(row)}>{row.student_name}</button> },
    { path: "student_phone", label: "تماس" },
    { path: "class_desc", label: "صنف" },
    { path: "subject_name", label: "مضمون" },
    { key: "percentage", label: "غیبت", content: row => <Percentage row={row} threshold={filters.warnPct} /> },
    { key: "status", label: "وضعیت", content: row => {
      const level = warningLevel(row, filters.warnPct);
      return <StatusBadge tone={level.tone}>{level.label}</StatusBadge>;
    } },
    { key: "warnedAt", label: "آخرین هشدار", content: row => row.warned_at || "ثبت نشده" },
    { key: "note", label: "یادداشت", content: row => row.note || "—" },
  ];
  const totalPages = Math.max(1, Math.ceil((body.summary?.total || 0) / 30));
  const report = body.summary?.report || {};
  const semester = report.semester || options.semester || (options.semesters || []).find((item) => String(item.entry_year_id) === String(entryYearId));
  const reportSubtitle = useMemo(() => {
    const parts = [];
    if (semester?.name) parts.push(`سمستر: ${semester.name}`);
    if (semester?.start_date && semester?.end_date) {
      parts.push(`${semester.start_date} تا ${semester.end_date}`);
    }
    if (semester?.week_number != null) parts.push(`هفته ${semester.week_number}`);
    parts.push(`پوهنحی: ${report.faculty_label || "همه"}`);
    parts.push(`صنف: ${report.class_label || "همه"}`);
    if (report.generated_at) parts.push(`تاریخ گزارش: ${report.generated_at}`);
    return parts.join(" · ");
  }, [semester, report.faculty_label, report.class_label, report.generated_at]);

  return (
    <div className="main-content attendance-admin warning-page mis-surface parity-list">
      {confirmationDialog}
      <div className="main-content-header">
        <div className="right-content">
          <MainTitle title="هشدار غیبت و محرومیت" />
          <p className="attendance-page-subtitle">
            محروم: غیبت بیش از ۲۵٪ از (کریدت نظری×۱۶). فقط جلسات نظری شمرده می‌شوند
          </p>
          {entryYearId ? <p className="attendance-page-subtitle warning-report-meta">{reportSubtitle}</p> : null}
        </div>
        <div className="center-content" />
        <div className="left-content attendance-header-links">
          <ParityReport
            print={false}
            title="هشدار غیبت و محرومیت"
            disabled={loading || denied || !entryYearId}
            subtitle={reportSubtitle}
            columns={[
              {label:'#',value:(_r,i)=>i+1},...[['student_name','محصل'],['student_father_name','ولد'],['student_phone','تماس'],['faculty_name','پوهنحی'],['class_desc','صنف'],['subject_name','مضمون']].map(([path,label])=>({path,label})),
              {label:'غیبت',value:r=>`${r.absent_count}/${r.total_count}`},{label:'فیصدی',value:r=>`${Math.round(r.absent_percentage)}٪`},{label:'وضعیت',value:r=>warningLevel(r,filters.warnPct).label},{label:'هشدار',value:r=>(r.warned_at || '').slice(0,16)},{path:'note',label:'یادداشت'}
            ]}
            loadRows={async()=>{const {data}=await db.getAll(`attendance/admin/warnings?${query()}&export=1`);return data.data;}}
          />
          <button
            type="button"
            className="mis-button is-secondary"
            disabled={loading || denied || !entryYearId}
            onClick={() => {
              const params = new URLSearchParams(query());
              params.delete("page");
              if (!openPrintRoute(`/print/absence-warnings?${params}`)) {
                flashPrintBlocked(setPrintError);
              }
            }}
          >
            چاپ
          </button>
          <Link className="mis-button is-secondary" to="/mis/results">
            بازگشت به نتایج
          </Link>
        </div>
      </div>
      {printError ? <p className="attendance-message" role="alert">{printError}</p> : null}

      <FilterBar
        className="attendance-filters warning-filters warning-filters-bare"
        title=""
        disabled={busy}
      >
        <label>
          سمستر
          <select
            aria-label="Semester"
            value={entryYearId}
            onChange={(e) => {
              setEntryYearId(e.target.value);
              change("classId", "");
            }}
          >
            <option value="">انتخاب سمستر</option>
            {(options.semesters || []).map((item) => (
              <option key={item.entry_year_id} value={item.entry_year_id}>
                {formatSemesterLabel(item)}
              </option>
            ))}
          </select>
          {formatSemesterDates(semester) ? (
            <small className="warning-semester-dates">{formatSemesterDates(semester)}</small>
          ) : null}
        </label>
        <label>
          پوهنحی
          <select
            aria-label="Faculty"
            value={filters.facultyId}
            onChange={(e) => {
              const facultyId = e.target.value;
              setFilters((previous) => ({
                ...previous,
                facultyId,
                classId: "",
              }));
              setPage(1);
            }}
          >
            <option value="">همه موارد مجاز</option>
            {(options.faculties || []).map((item) => (
              <option key={item.faculty_id} value={item.faculty_id}>
                {item.faculty_name}
              </option>
            ))}
          </select>
        </label>
        <label>
          صنف
          <select
            aria-label="Class"
            value={filters.classId}
            onChange={(e) => change("classId", e.target.value)}
          >
            <option value="">همه موارد مجاز</option>
            {(options.classes || [])
              .filter(
                (item) =>
                  !filters.facultyId ||
                  Number(item.faculty) === Number(filters.facultyId)
              )
              .map((item) => (
                <option key={item.class_id} value={item.class_id}>
                  {item.class_desc}
                </option>
              ))}
          </select>
        </label>
        <label>
          آستانه هشدار
          <div className="warning-threshold-field">
            <input
              aria-label="Warning threshold"
              type="number"
              min="1"
              max="100"
              value={filters.warnPct}
              onChange={(e) => change("warnPct", e.target.value)}
            />
            <span>٪</span>
          </div>
        </label>
        <label>
          جستجوی محصل
          <input
            className="parity-search-input"
            aria-label="Search students"
            placeholder="نام، شماره یا تماس"
            value={filters.search}
            onChange={(e) => change("search", e.target.value)}
          />
        </label>
        <label>
          از تاریخ
          <input
            aria-label="From date"
            type="date"
            value={filters.dateFrom}
            onChange={(e) => change("dateFrom", e.target.value)}
          />
        </label>
        <label>
          تا تاریخ
          <input
            aria-label="To date"
            type="date"
            value={filters.dateTo}
            onChange={(e) => change("dateTo", e.target.value)}
          />
        </label>
        <label>
          وضعیت
          <select
            aria-label="Warning status"
            value={filters.status}
            onChange={(e) => change("status", e.target.value)}
          >
            <option value="all">همه وضعیت‌ها</option>
            <option value="warn">هشدار</option>
            <option value="disqualified">محروم</option>
            <option value="warned">هشدار ثبت شده</option>
            <option value="unwarned">بدون هشدار ثبت‌شده</option>
          </select>
        </label>
        <label>
          نمایش مضمون
          <select
            aria-label="Subject grouping"
            value={filters.worstOnly}
            onChange={(e) => change("worstOnly", e.target.value)}
          >
            <option value="1">بیشترین غیبت هر محصل</option>
            <option value="0">همه مضامین</option>
          </select>
        </label>
        <button
          className="mis-button is-secondary"
          type="button"
          disabled={loading}
          onClick={() => refresh()}
        >
          بارگیری مجدد
        </button>
      </FilterBar>

      {message ? (
        <p className="attendance-message" role="status" aria-live="polite">
          {message}
        </p>
      ) : null}
      {body.summary?.capped ? (
        <p className="attendance-message is-warning">
          نتایج به ۲۵۰۰ ردیف محدود شده است؛ فیلتر جستجو را دقیق‌تر کنید.
        </p>
      ) : null}

      <SectionCard
        className="attendance-table-card warning-table-card"
        title="محصلان نیازمند بررسی"
        subtitle="برای مشاهده جزئیات هر مضمون، نام محصل را انتخاب کنید."
        actions={
          <StatusBadge tone="info">{body.summary?.total || 0} نتیجه</StatusBadge>
        }
      >
        {denied ? (
          <AccessDeniedResponse />
        ) : loading ? (
          <LoadingState />
        ) : !entryYearId ? (
          <EmptyState
            title="سمستر را انتخاب کنید"
            description="برای مشاهده درصد غیبت محصلان، یک سمستر را از بخش فیلتر انتخاب کنید."
          />
        ) : body.data.length === 0 ? (
          <EmptyState
            title="محصلی با این فیلترها پیدا نشد"
            description="محدوده تاریخ، آستانه یا وضعیت هشدار را تغییر دهید."
          />
        ) : (
          <div className="main-content-body attendance-table-wrap warning-table-wrap">
            <Table data={body.data} columns={columns} />
          </div>
        )}
      </SectionCard>

      <ActionGroup className="attendance-pagination" aria-label="صفحه‌بندی">
        <button
          className="mis-button is-secondary"
          disabled={busy || loading || page <= 1}
          onClick={() => setPage((p) => p - 1)}
        >
          صفحه قبلی
        </button>
        <span>
          صفحه {body.summary?.page || page} از {totalPages}
        </span>
        <button
          className="mis-button is-secondary"
          disabled={
            busy ||
            loading ||
            page * 30 >= (body.summary?.total || 0)
          }
          onClick={() => setPage((p) => p + 1)}
        >
          صفحه بعدی
        </button>
      </ActionGroup>

      {detail ? (
        <div
          className="attendance-dialog-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !busy) setDetail(null);
          }}
        >
          <section
            className="attendance-dialog warning-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="warning-dialog-title"
            onKeyDown={(e) => {
              if (e.key === "Escape" && !busy) setDetail(null);
            }}
          >
            <header className="attendance-dialog-header">
              <div>
                <span className="attendance-eyebrow">جزئیات غیبت محصل</span>
                <h3 id="warning-dialog-title">{detail.name}</h3>
                <p>یادداشت هشدار را برای هر مضمون جداگانه ثبت کنید.</p>
              </div>
              <button
                className="attendance-dialog-close"
                type="button"
                aria-label="بستن"
                disabled={busy}
                onClick={() => setDetail(null)}
              >
                ×
              </button>
            </header>
            {message ? <p className="attendance-message" role="alert">{message}</p> : null}
            <div className="warning-subjects">
              {detail.data.map((row, index) => {
                const level = warningLevel(row, filters.warnPct);
                return (
                  <article
                    className="warning-subject-card"
                    key={`${row.subject_id}-${index}`}
                  >
                    <header>
                      <div>
                        <span>مضمون</span>
                        <h4>{row.subject_name}</h4>
                      </div>
                      <StatusBadge tone={level.tone}>{level.label}</StatusBadge>
                    </header>
                    <Percentage row={row} threshold={filters.warnPct} />
                    <label className="mis-dialog-field">
                      یادداشت هشدار
                      <textarea
                        aria-label={`Warning note ${row.subject_id}`}
                        disabled={busy}
                        maxLength="512"
                        value={notes[row.subject_id] ?? row.note ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotes((previous) => ({
                            ...previous,
                            [row.subject_id]: value,
                          }));
                        }}
                      />
                    </label>
                    <div className="warning-save-row">
                      <small>حداکثر ۵۱۲ حرف</small>
                      <button
                        className="mis-button"
                        disabled={busy}
                        data-testid={`save-warning-${row.subject_id}`}
                        onClick={() => save(row)}
                      >
                        {busy ? "در حال ثبت…" : "ثبت هشدار"}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>

            <section className="warning-history">
              <header>
                <h4>تاریخچه هشدارها</h4>
                <span>۵۰ ثبت اخیر</span>
              </header>
              {(detail.summary?.history || []).length ? (
                <ol>
                  {detail.summary.history.map((item, index) => (
                    <li key={`${item.warned_at || "history"}-${index}`}>
                      <span className="warning-history-dot" aria-hidden="true" />
                      <div>
                        <strong>{item.warned_by_name || item.warned_by || "—"}</strong>
                        <p>{item.note || "بدون یادداشت"}</p>
                        <small>
                          {item.warned_at || "—"} · مضمون {item.subject_id}
                        </small>
                      </div>
                    </li>
                  ))}
                </ol>
              ) : (
                <EmptyState
                  title="تاریخچه‌ای ثبت نشده است"
                  description="پس از ثبت هشدار، جزئیات آن در این بخش نمایش داده می‌شود."
                />
              )}
            </section>
            <ActionGroup className="attendance-dialog-actions">
              <button
                className="mis-button is-secondary"
                disabled={busy}
                onClick={() => setDetail(null)}
              >
                بستن
              </button>
            </ActionGroup>
          </section>
        </div>
      ) : null}
    </div>
  );
}
